import React, { useState, useEffect } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Drawer from "@mui/material/Drawer";
import DrawerFooterContainerLarge from "../../requests/shared/helpers/DrawerFooterContainerLarge";
import ButtonOutlined from "../../requests/shared/helpers/ButtonOutlined";
import HistoricalSalesContextCard from "./HistoricalSalesContextCard";
import AdjustmentsHistoryTooltip from "./AdjustmentsHistoryTooltip";
import ManualIntradayDistributionModal from "./ManualIntradayDistributionModal";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SouthEastIcon from "@mui/icons-material/SouthEast";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";

// Smaller icon for forecast adjustment
function adjustment_icon_small(value) {
  if (value.toFixed(3) === "0.000") {
    return "";
  }
  if (value < -0.2) {
    return <SouthEastIcon style={{ fontSize: "16px" }} />;
  }
  if (value < 0) {
    return <TrendingDownIcon style={{ fontSize: "16px" }} />;
  }
  if (value > 0.2) {
    return <NorthEastIcon style={{ fontSize: "16px" }} />;
  }
  if (value > 0) {
    return <TrendingUpIcon style={{ fontSize: "16px" }} />;
  } else {
    return "";
  }
}

function formatReservationAverage(value) {
  if (!Number.isFinite(value)) return "0";
  if (Math.abs(value - Math.round(value)) < 0.05) {
    return Math.round(value).toString();
  }
  return value.toFixed(1);
}

function formatSignedReservationDelta(value) {
  if (!Number.isFinite(value)) return "--";
  const absoluteValue = Math.abs(value);
  const formattedValue = absoluteValue < 10 && Math.abs(value - Math.round(value)) >= 0.05
    ? absoluteValue.toFixed(1)
    : Math.round(absoluteValue).toString();
  if (Math.abs(value) < 0.05) return "0";
  return `${value > 0 ? "+" : "-"}${formattedValue}`;
}

function formatShortDate(dateString) {
  if (!dateString) return "";
  const parsedDate = new Date(`${dateString}T12:00:00`);
  if (Number.isNaN(parsedDate.getTime())) return dateString;
  return parsedDate.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function toFiniteNumber(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function ForecastAdjustmentDrawer(props) {
  // Calculate initial state and forecast
  const initial_state =
    props.adjustment_chosen.length === 0
      ? 0
      : props.adjustment_chosen[0]["adjustment_percent_value"];

  const initial_reason =
    props.adjustment_chosen.length === 0
      ? ""
      : props.adjustment_chosen[0]["reason"];

  const calculateInitialForecast = () => {
    if (
      props.adjustment_chosen.length > 0 &&
      props.forecast_sales === 0 &&
      props.adjustment_chosen[0]["sales_value"]
    ) {
      const manualValue = parseFloat(props.adjustment_chosen[0]["sales_value"]);
      if (!isNaN(manualValue) && manualValue > 0) {
        return manualValue;
      }
    }
    return props.forecast_sales * initial_state + props.forecast_sales;
  };
  const initial_forecast = calculateInitialForecast();

  // State
  const [value, setValue] = useState(initial_state);
  const [savingLoading, setSavingLoading] = useState(false);
  const [adjustedForecast, setAdjustedForecast] = useState(
    initial_forecast.toFixed(0)
  );
  const [reason, setReason] = useState(initial_reason);
  const [note, setNote] = useState(
    props.adjustment_chosen.length === 0
      ? ""
      : props.adjustment_chosen[0]["note"] || ""
  );
  const [showForecastAdjustmentInputs, setShowForecastAdjustmentInputs] =
    useState(false);
  const [openManualIntradayModal, setOpenManualIntradayModal] = useState(false);
  const [openManualActualSalesModal, setOpenManualActualSalesModal] = useState(false);
  const [editManualActualSalesValue, setEditManualActualSalesValue] = useState(
    props.actual_sales || 0
  );
  const [savingManualActualSales, setSavingManualActualSales] = useState(false);

  const parsedManualActualSalesValue = Number(editManualActualSalesValue);
  const isManualActualSalesValueValid =
    editManualActualSalesValue !== "" &&
    Number.isFinite(parsedManualActualSalesValue) &&
    parsedManualActualSalesValue >= 0;

  // Update state when props change
  useEffect(() => {
    const initial_state =
      props.adjustment_chosen.length === 0
        ? 0
        : props.adjustment_chosen[0]["adjustment_percent_value"];
    const initial_reason =
      props.adjustment_chosen.length === 0
        ? ""
        : props.adjustment_chosen[0]["reason"];
    const initial_note =
      props.adjustment_chosen.length === 0
        ? ""
        : props.adjustment_chosen[0]["note"] || "";
    const initial_forecast =
      props.adjustment_chosen.length > 0 &&
      props.forecast_sales === 0 &&
      props.adjustment_chosen[0]["sales_value"] &&
      parseFloat(props.adjustment_chosen[0]["sales_value"]) > 0
        ? parseFloat(props.adjustment_chosen[0]["sales_value"])
        : props.forecast_sales * initial_state + props.forecast_sales;

    setValue(initial_state);
    setReason(initial_reason);
    setNote(initial_note);
    setAdjustedForecast(initial_forecast.toFixed(0));
  }, [props.adjustment_chosen, props.forecast_sales]);

  // Reset when adjustment finished
  useEffect(() => {
    if (props.addAdjustmentFinished) {
      setSavingLoading(false);
      props.setOpenForecastAdjustment(false);
    }
  }, [props.addAdjustmentFinished]);

  // Derived values
  const adjustments_history_manual = props.adjustments_history?.filter(
    (adjustment) => adjustment?.adjustment_origin === "manual"
  );
  const adjustments_history_internal = props.adjustments_history?.filter(
    (adjustment) => adjustment?.adjustment_origin === "internal"
  );

  const hasManualInitialForecast =
    props.forecast_sales === 0 &&
    props.manualSalesInputEnabled &&
    props.adjustment_chosen.length > 0 &&
    props.adjustment_chosen[0]["sales_value"] &&
    parseFloat(props.adjustment_chosen[0]["sales_value"]) > 0;

  const hasForecastedSalesValue =
    parseFloat(props.forecast_sales || 0) > 0 || hasManualInitialForecast;

  const hasSalesDataForIntradayModal =
    parseFloat(props.forecast_sales || 0) > 0 ||
    parseFloat(props.actual_sales || 0) > 0 ||
    hasManualInitialForecast ||
    parseFloat(adjustedForecast || 0) > 0;

  const canOpenManualIntradayModal =
    props.manualSalesInputEnabled && hasForecastedSalesValue;

  const showForecastBreakdownLink =
    props.forecast_sales !== 0 && !props.manualSalesInputEnabled;

  const canEditManualActualSales =
    props.manualSalesInputEnabled && props.day_in_past;

  let actualColor = "";
  if (initial_forecast <= props.actual_sales) {
    actualColor = "text-emerald-700";
  } else {
    actualColor = "text-rose-700";
  }

  const percentDifference =
    ((initial_forecast - props.forecast_sales) / props.forecast_sales) * 100;

  const reservationSummaries = props.reservation_summaries || [];
  const selectedReservationSummary = props.reservation_summary || reservationSummaries.find(
    (summary) => summary?.date === props.date_str
  ) || null;
  const holidayName =
    props.historical_sales_context?.[0]?.holidays?.[0]?.holiday_name ||
    selectedReservationSummary?.holiday_name;
  const isHoliday =
    props.historical_sales_context?.[0]?.holidays?.length > 0 || !!selectedReservationSummary?.holiday_name;
  const selectedDate = props.date_str ? new Date(`${props.date_str}T12:00:00`) : null;
  const selectedDayOfWeek = selectedDate ? selectedDate.getDay() : null;
  const comparableDowSummaries = reservationSummaries.filter((summary) => {
    if (!summary?.date || !Number.isFinite(selectedDayOfWeek) || summary.date === props.date_str) {
      return false;
    }
    const summaryDate = new Date(`${summary.date}T12:00:00`);
    return summaryDate.getDay() === selectedDayOfWeek;
  });
  const avgReservationsSameDow = comparableDowSummaries.length
    ? comparableDowSummaries.reduce((sum, summary) => sum + (summary.total_reservations || 0), 0) /
      comparableDowSummaries.length
    : null;
  const avgCoversSameDow = comparableDowSummaries.length
    ? comparableDowSummaries.reduce((sum, summary) => sum + (summary.total_covers || 0), 0) /
      comparableDowSummaries.length
    : null;
  const reservationDeltaVsDaysOutAvg = selectedReservationSummary?.reservation_delta_vs_days_out_avg;
  const coversDeltaVsDaysOutAvg = selectedReservationSummary?.covers_delta_vs_days_out_avg;
  const daysOutComparableCount = selectedReservationSummary?.days_out_comparable_count;
  const daysOut = selectedReservationSummary?.days_out;
  const selectedReservations = toFiniteNumber(selectedReservationSummary?.total_reservations);
  const selectedCovers = toFiniteNumber(selectedReservationSummary?.total_covers);
  const priorHolidayReservationsSameDaysOut = toFiniteNumber(
    selectedReservationSummary?.prior_holiday_reservations_same_days_out
  );
  const priorHolidayCoversSameDaysOut = toFiniteNumber(
    selectedReservationSummary?.prior_holiday_covers_same_days_out
  );
  const hasPriorHolidayReservationContext = isHoliday && (
    Number.isFinite(priorHolidayReservationsSameDaysOut) ||
    Number.isFinite(priorHolidayCoversSameDaysOut)
  );
  const hasReservationDaysOutContext =
    Number.isFinite(reservationDeltaVsDaysOutAvg) ||
    Number.isFinite(coversDeltaVsDaysOutAvg);
  const reservationAverageToShow = hasPriorHolidayReservationContext
    ? priorHolidayReservationsSameDaysOut
    : hasReservationDaysOutContext
      ? selectedReservationSummary?.avg_reservations_same_dow_days_out
      : avgReservationsSameDow;
  const coversAverageToShow = hasPriorHolidayReservationContext
    ? priorHolidayCoversSameDaysOut
    : hasReservationDaysOutContext
      ? selectedReservationSummary?.avg_covers_same_dow_days_out
      : avgCoversSameDow;
  const reservationDeltaToShow =
    Number.isFinite(selectedReservations) && Number.isFinite(reservationAverageToShow)
      ? selectedReservations - reservationAverageToShow
      : null;
  const coversDeltaToShow =
    Number.isFinite(selectedCovers) && Number.isFinite(coversAverageToShow)
      ? selectedCovers - coversAverageToShow
      : null;
  const reservationDeltaRatioToShow =
    Number.isFinite(reservationDeltaToShow) && reservationAverageToShow > 0
      ? reservationDeltaToShow / reservationAverageToShow
      : 0;
  const coversDeltaRatioToShow =
    Number.isFinite(coversDeltaToShow) && coversAverageToShow > 0
      ? coversDeltaToShow / coversAverageToShow
      : 0;
  const coversPerReservation = selectedReservationSummary?.total_reservations > 0
    ? selectedReservationSummary.total_covers / selectedReservationSummary.total_reservations
    : null;
  const hasMeaningfulReservationContext = !!selectedReservationSummary && (
    (selectedReservationSummary.total_reservations || 0) > 0 ||
    (selectedReservationSummary.total_covers || 0) > 0 ||
    (reservationAverageToShow || 0) > 0 ||
    (coversAverageToShow || 0) > 0
  );
  const reservationSectionTitle = hasReservationDaysOutContext && Number.isFinite(daysOut)
    ? `Reservations at ${daysOut} days out`
    : `Reservations for ${props.fullDayString}s`;
  const reservationCardContextLabel = hasReservationDaysOutContext && Number.isFinite(daysOut)
    ? `${daysOut} days out`
    : `${props.fullDayString}s`;
  const reservationComparisonLabel = hasPriorHolidayReservationContext
    ? `last ${holidayName}`
    : "avg";
  const reservationComparableLabel = hasPriorHolidayReservationContext
    ? `Compared with ${holidayName} on ${formatShortDate(selectedReservationSummary?.prior_holiday_date)} at the same days-out point`
    : hasReservationDaysOutContext
    ? `Based on ${daysOutComparableCount} comparable ${props.fullDayString.toLowerCase()}${daysOutComparableCount === 1 ? "" : "s"} at the same days-out point`
    : `Based on ${comparableDowSummaries.length} comparable ${props.fullDayString.toLowerCase()}${comparableDowSummaries.length === 1 ? "" : "s"}`;

  // Functions
  function handleClose() {
    const initial_state =
      props.adjustment_chosen.length === 0
        ? 0
        : props.adjustment_chosen[0]["adjustment_percent_value"];
    const initial_reason =
      props.adjustment_chosen.length === 0
        ? ""
        : props.adjustment_chosen[0]["reason"];
    const initial_note =
      props.adjustment_chosen.length === 0
        ? ""
        : props.adjustment_chosen[0]["note"] || "";
    const initial_forecast =
      props.adjustment_chosen.length > 0 &&
      props.forecast_sales === 0 &&
      props.adjustment_chosen[0]["sales_value"] &&
      parseFloat(props.adjustment_chosen[0]["sales_value"]) > 0
        ? parseFloat(props.adjustment_chosen[0]["sales_value"])
        : props.forecast_sales * initial_state + props.forecast_sales;

    setValue(initial_state);
    setReason(initial_reason);
    setNote(initial_note);
    setAdjustedForecast(initial_forecast.toFixed(0));
    setShowForecastAdjustmentInputs(false);
    props.setOpenForecastAdjustment(false);
  }

  function submitAndClose() {
    setSavingLoading(true);
    let add_type = "new";
    let data = {
      date: props.date_str,
      adjustments_chosen: "adjust",
      adjustment_percent_value: value,
      sales_value: adjustedForecast,
      reason: value === 0 ? "" : reason,
      note: note,
    };

    if (props.adjustment_chosen.length > 0) {
      add_type = "edit";
      data = props.adjustment_chosen[0];
      data["adjustment_percent_value"] = value;
      data["sales_value"] = adjustedForecast;
      data["reason"] = value === 0 ? "" : reason;
      data["note"] = note;
    }

    props.addForecastAdjustment(data, add_type);
  }

  function handleSaveManualActualSales() {
    const salesValue = parseFloat(editManualActualSalesValue);

    if (isNaN(salesValue) || salesValue < 0) {
      alert("Please enter a valid non-negative number for actual sales.");
      return;
    }

    setSavingManualActualSales(true);

    const data = {
      date: props.date_str,
      sales_value: salesValue,
    };

    props.addManualActualSales(data)
      .then(() => {
        setSavingManualActualSales(false);
        setOpenManualActualSalesModal(false);
      })
      .catch((error) => {
        setSavingManualActualSales(false);
        const errorMessage = error.response?.data?.error || "Failed to save manual actual sales. Please try again.";
        alert(errorMessage);
      });
  }

  function openManualActualSalesEditor() {
    setEditManualActualSalesValue(props.actual_sales || 0);
    setOpenManualActualSalesModal(true);
  }

  function toggleShowForecastAdjustmentInputs() {
    setShowForecastAdjustmentInputs((prevState) => !prevState);
  }

  return (
    <Drawer
      anchor="right"
      open={props.open}
      onClose={handleClose}
    >
      <div
        data-help-id="forecast-adjustment-drawer"
        className="w-[450px] h-full"
      >
        <div className="max-h-screen overflow-auto">
          <div className="bg-white">
            <div className="flex items-start justify-between px-6 pt-7 pb-3">
              <div>
                <p className="text-lg font-semibold text-slate-900">{props.date_name}</p>
                <p className="text-xs text-slate-500">
                  {props.isClosed ? "Closed" : props.hooString}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {isHoliday && (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-[10px] font-medium text-amber-700 border border-amber-200">
                    {props.historical_sales_context?.[0]?.holidays?.[0]?.holiday_name}
                  </span>
                )}
                <div className="text-slate-400 cursor-pointer hover:text-slate-600 transition-colors" onClick={handleClose}>
                  <CloseIcon style={{ fontSize: "20px" }} />
                </div>
              </div>
            </div>
            <div className="px-6 pb-4">
              {(showForecastBreakdownLink || canOpenManualIntradayModal) && (
                <div className="flex flex-wrap gap-2">
                  {showForecastBreakdownLink && (
                    <button
                      onClick={() => props.onOpenAdvancedModal?.()}
                      className="text-[11px] font-medium text-violet-700 hover:bg-violet-100 bg-violet-50 border border-violet-200 rounded-full px-3 py-1 cursor-pointer transition-colors"
                    >
                      See forecast breakdown
                    </button>
                  )}
                  {canOpenManualIntradayModal && (
                    <button
                      onClick={() => setOpenManualIntradayModal(true)}
                      className="text-[11px] text-violet-700 hover:text-violet-800 underline underline-offset-2 cursor-pointer"
                    >
                      See and set intraday sales distribution
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="w-full px-6">
            {!props.day_in_past ? (
              <div className="w-full border-t border-slate-100 pt-4 pb-4">
                <div className="w-full flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-1">
                      <p className="text-[10px] text-slate-500">
                        {hasManualInitialForecast
                          ? "Manual forecast"
                          : props.forecast_sales === 0 &&
                            props.manualSalesInputEnabled
                          ? "No forecast available"
                          : "Rightwork forecast"}
                      </p>
                      {adjustments_history_internal?.length > 0 &&
                        !hasManualInitialForecast && (
                          <AdjustmentsHistoryTooltip
                            initial_forecast={props.forecast_sales}
                            adjustments_history={adjustments_history_internal}
                            internal={true}
                          />
                        )}
                      {adjustments_history_manual?.length > 0 &&
                        hasManualInitialForecast && (
                          <AdjustmentsHistoryTooltip
                            initial_forecast={props.forecast_sales}
                            adjustments_history={adjustments_history_manual}
                          />
                        )}
                    </div>
                    {props.forecast_sales === 0 &&
                    props.manualSalesInputEnabled &&
                    !hasManualInitialForecast ? (
                      <p className="mt-0.5 text-slate-400 text-sm italic">
                        POS data not available
                      </p>
                    ) : (
                      <p className="text-xl font-bold text-slate-900">
                        {hasManualInitialForecast
                          ? initial_forecast.toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })
                          : props.forecast_sales.toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}
                      </p>
                    )}
                  </div>
                  {props.forecast_sales !== initial_forecast &&
                    !hasManualInitialForecast && (
                      <div>
                        <div className="flex items-center gap-1">
                          <p className="text-[10px] text-slate-500">
                            Adjusted forecast
                          </p>
                          {adjustments_history_manual?.length > 0 && (
                            <AdjustmentsHistoryTooltip
                              initial_forecast={props.forecast_sales}
                              adjustments_history={adjustments_history_manual}
                            />
                          )}
                        </div>
                        <p className="text-xl font-bold text-slate-900">
                          {initial_forecast.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </p>
                        <div
                          className={`flex items-center gap-1 ${
                            percentDifference < 0
                              ? "text-rose-600"
                              : "text-emerald-600"
                          }`}
                        >
                          <div className="mb-0.5">
                            {adjustment_icon_small(percentDifference / 100)}
                          </div>
                          <p className="text-[10px] font-medium">
                            {`${Math.abs(percentDifference).toFixed(0)}%`}{" "}
                            <span className="text-slate-400 font-normal">
                              {percentDifference >= 0
                                ? "increase"
                                : "decrease"}
                            </span>
                          </p>
                        </div>
                      </div>
                    )}
                  {/* Empty placeholder to match 3-column layout with past-day view */}
                  <div className="w-[92px]"></div>
                </div>
                {!showForecastAdjustmentInputs &&
                  !props.day_in_past &&
                  (props.forecast_sales !== 0 ||
                    (props.forecast_sales === 0 &&
                      props.manualSalesInputEnabled)) && (
                    <div
                      className="mt-4 bg-violet-600 hover:bg-violet-500 text-white py-2 px-4 rounded-lg flex items-center justify-center cursor-pointer transition-colors"
                      onClick={toggleShowForecastAdjustmentInputs}
                    >
                      <p className="text-sm font-medium">
                        {hasManualInitialForecast
                          ? "Update forecast"
                          : props.forecast_sales === 0 &&
                            props.manualSalesInputEnabled
                          ? "Set manual forecast"
                          : props.forecast_sales === initial_forecast
                          ? "Adjust initial forecast"
                          : "Change adjusted forecast"}
                      </p>
                    </div>
                  )}
              </div>
            ) : (
              <div className="w-full border-t border-slate-100 pt-4 pb-4">
                <div className="w-full flex items-end justify-between">
                  <div>
                    <div className="flex items-center gap-1">
                      <p className="text-[10px] text-slate-500">
                        {hasManualInitialForecast
                          ? "Manual forecast"
                          : "Rightwork forecast"}
                      </p>
                      {adjustments_history_internal?.length > 0 &&
                        !hasManualInitialForecast && (
                          <AdjustmentsHistoryTooltip
                            initial_forecast={props.forecast_sales}
                            adjustments_history={adjustments_history_internal}
                            internal={true}
                          />
                        )}
                      {adjustments_history_manual?.length > 0 &&
                        hasManualInitialForecast && (
                          <AdjustmentsHistoryTooltip
                            initial_forecast={props.forecast_sales}
                            adjustments_history={adjustments_history_manual}
                          />
                        )}
                    </div>
                    <p className="text-xl font-bold text-slate-900">
                      {hasManualInitialForecast
                        ? initial_forecast.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })
                        : props.forecast_sales.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                    </p>
                  </div>
                  {props.forecast_sales !== initial_forecast &&
                    !hasManualInitialForecast && (
                      <div>
                        <div className="flex items-center gap-1">
                          <p className="text-[10px] text-slate-500">
                            Adjusted
                          </p>
                          {adjustments_history_manual?.length > 0 && (
                            <AdjustmentsHistoryTooltip
                              initial_forecast={props.forecast_sales}
                              adjustments_history={adjustments_history_manual}
                            />
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <p className="text-xl font-bold text-slate-900">
                            {initial_forecast.toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}
                          </p>
                          {Math.abs(initial_forecast - props.actual_sales) <=
                          Math.abs(
                            props.forecast_sales - props.actual_sales
                          ) ? (
                            <span className="text-green-600 text-lg">👍</span>
                          ) : (
                            <span className="text-rose-600 text-lg">👎
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  <div className="min-w-[132px] text-right">
                    <div className="flex items-center justify-end gap-1">
                      <p className="text-[10px] text-slate-500">
                        Actual sales
                      </p>
                      {canEditManualActualSales && (
                        <button
                          type="button"
                          onClick={openManualActualSalesEditor}
                          aria-label={`Edit actual sales for ${props.date_name}`}
                          title="Edit actual sales"
                          className="inline-flex h-5 w-5 items-center justify-center rounded text-violet-600 transition-colors hover:bg-violet-50 hover:text-violet-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-1"
                        >
                          <EditIcon style={{ fontSize: "13px" }} />
                        </button>
                      )}
                    </div>
                    <p className={`text-xl font-bold ${actualColor}`}>
                      {"$" +
                        props.actual_sales.toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        })}
                    </p>
                  </div>
                </div>
                {/* Accuracy improvement insight */}
                {props.forecast_sales !== initial_forecast &&
                  !hasManualInitialForecast && (
                    <div className={`mt-3 rounded-lg border p-3 ${
                      Math.abs(initial_forecast - props.actual_sales) <=
                      Math.abs(props.forecast_sales - props.actual_sales)
                        ? "border-violet-200 bg-violet-50"
                        : "border-rose-200 bg-rose-50"
                    }`}>
                      <div className="flex items-start gap-2">
                        {Math.abs(initial_forecast - props.actual_sales) <=
                        Math.abs(props.forecast_sales - props.actual_sales) ? (
                          <>
                            <TrendingUpIcon style={{ fontSize: "16px" }} className="mt-0.5 flex-shrink-0 text-violet-600" />
                            <p className="text-[11px] text-violet-700">
                              Adjustment improved forecast accuracy by{" "}
                              <span className="font-semibold">
                                {(
                                  Math.abs(props.forecast_sales - props.actual_sales) -
                                  Math.abs(initial_forecast - props.actual_sales)
                                ).toLocaleString("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                })}
                              </span>
                            </p>
                          </>
                        ) : (
                          <>
                            <TrendingDownIcon style={{ fontSize: "16px" }} className="mt-0.5 flex-shrink-0 text-rose-600" />
                            <p className="text-[11px] text-rose-700">
                              Adjustment reduced forecast accuracy by{" "}
                              <span className="font-semibold">
                                {(
                                  Math.abs(initial_forecast - props.actual_sales) -
                                  Math.abs(props.forecast_sales - props.actual_sales)
                                ).toLocaleString("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                })}
                              </span>
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  )}
              </div>
            )}
            {showForecastAdjustmentInputs && (
              <div className="w-full py-4 border-t border-slate-100">
                <div>
                  <h3 className="text-[10px] text-slate-500 uppercase tracking-wide font-medium">
                    {hasManualInitialForecast
                      ? "Update forecast"
                      : props.forecast_sales === 0 &&
                        props.manualSalesInputEnabled
                      ? "Set initial forecast"
                      : props.forecast_sales === initial_forecast
                      ? "Set new forecast"
                      : "Set new adjusted forecast"}
                  </h3>
                  <div className="mt-1">
                    <TextField
                      fullWidth
                      id="outlined-basic"
                      variant="outlined"
                      placeholder="$10,000"
                      size="small"
                      InputProps={{
                        sx: {
                          backgroundColor: "white",
                          borderRadius: "8px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                        },
                      }}
                      value={"$" + adjustedForecast}
                      onChange={(event) => {
                        const inputValue = event.target.value;
                        const numericValue = inputValue.replace(
                          /[^0-9.]/g,
                          ""
                        );
                        setAdjustedForecast(numericValue);

                        if (props.forecast_sales === 0) {
                          setValue(0);
                        } else {
                          setValue(
                            (parseFloat(numericValue) - props.forecast_sales) /
                              props.forecast_sales
                          );
                        }
                      }}
                    />
                  </div>
                </div>
                {!(
                  props.forecast_sales === 0 && props.manualSalesInputEnabled
                ) && (
                  <div className="mt-3">
                    <h3 className="text-[10px] text-slate-500 uppercase tracking-wide font-medium">Reason*</h3>
                    <div className="mt-1">
                      <FormControl
                        fullWidth
                        size="small"
                        sx={{
                          borderRadius: "12px",
                          backgroundColor: "white",
                        }}
                      >
                        <Select
                          value={reason}
                          onChange={(event) => setReason(event.target.value)}
                          displayEmpty
                          sx={{
                            borderRadius: "8px",
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#6d28d9",
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            Reason for change
                          </MenuItem>
                          <MenuItem value="weather">Weather</MenuItem>
                          <MenuItem value="events">Local events</MenuItem>
                          <MenuItem value="trust">
                            Don't trust forecast
                          </MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                )}
                <div className="mt-3">
                  <h3 className="text-[10px] text-slate-500 uppercase tracking-wide font-medium">Note (optional)</h3>
                  <div className="mt-1">
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      variant="outlined"
                      placeholder="Add additional context or details..."
                      size="small"
                      inputProps={{
                        maxLength: 500,
                      }}
                      InputProps={{
                        sx: {
                          backgroundColor: "white",
                          borderRadius: "8px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                        },
                      }}
                      value={note}
                      onChange={(event) => setNote(event.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}
            {/* Historical context sections */}
            {isHoliday && (
              <div className="border-t border-slate-100 pt-4 mt-2">
                <p className="text-sm font-semibold text-slate-900 mb-2">
                  This{" "}
                  {
                    props.historical_sales_context?.[0]?.holidays?.[0]
                      ?.holiday_name
                  }{" "}
                  last year
                </p>
                <HistoricalSalesContextCard
                  date={
                    props.historical_sales_context?.[0]?.holidays?.[0]
                      ?.last_year_date
                  }
                  initial_forecast={
                    props.historical_sales_context?.[0]?.holidays?.[0]
                      ?.initial_forecasted?.value
                  }
                  adjusted_forecast={
                    props.historical_sales_context?.[0]?.holidays?.[0]
                      ?.forecasted_adjustment
                  }
                  actual_sales={
                    props.historical_sales_context?.[0]?.holidays?.[0]
                      ?.actual_sales?.value
                  }
                  weather={
                    props.historical_sales_context?.[0]?.holidays?.[0]
                      ?.weather
                  }
                  events={
                    props.historical_sales_context?.[0]?.holidays?.[0]
                      ?.events
                  }
                  holiday={true}
                  holidayName={
                    props.historical_sales_context?.[0]?.holidays?.[0]
                      ?.holiday_name
                  }
                />
              </div>
            )}
            <div className="border-t border-slate-100 pt-4 mt-2">
              <p className="text-sm font-semibold text-slate-900 mb-2">
                This {props.fullDayString} last year
              </p>
              <HistoricalSalesContextCard
                date={
                  props.historical_sales_context?.[0]?.one_year_ago?.date
                }
                initial_forecast={
                  props.historical_sales_context?.[0]?.one_year_ago
                    ?.initial_forecasted?.value
                }
                adjusted_forecast={
                  props.historical_sales_context?.[0]?.one_year_ago
                    ?.forecasted_adjustment
                }
                actual_sales={
                  props.historical_sales_context?.[0]?.one_year_ago
                    ?.actual_sales?.value
                }
                weather={
                  props.historical_sales_context?.[0]?.one_year_ago
                    ?.weather
                }
                events={
                  props.historical_sales_context?.[0]?.one_year_ago?.events
                }
              />
            </div>
            <div className="border-t border-slate-100 pt-4 mt-4">
              <p className="text-sm font-semibold text-slate-900 mb-2">
                Last 4 {props.fullDayString}s
              </p>
              <div className="space-y-2 pb-5">
                <HistoricalSalesContextCard
                  date={
                    props.historical_sales_context?.[0]?.one_week_ago?.date
                  }
                  initial_forecast={
                    props.historical_sales_context?.[0]?.one_week_ago
                      ?.initial_forecasted?.value
                  }
                  adjusted_forecast={
                    props.historical_sales_context?.[0]?.one_week_ago
                      ?.forecasted_adjustment
                  }
                  actual_sales={
                    props.historical_sales_context?.[0]?.one_week_ago
                      ?.actual_sales?.value
                  }
                  weather={
                    props.historical_sales_context?.[0]?.one_week_ago
                      ?.weather
                  }
                  events={
                    props.historical_sales_context?.[0]?.one_week_ago?.events
                  }
                />
                <HistoricalSalesContextCard
                  date={
                    props.historical_sales_context?.[0]?.two_weeks_ago?.date
                  }
                  initial_forecast={
                    props.historical_sales_context?.[0]?.two_weeks_ago
                      ?.initial_forecasted?.value
                  }
                  adjusted_forecast={
                    props.historical_sales_context?.[0]?.two_weeks_ago
                      ?.forecasted_adjustment
                  }
                  actual_sales={
                    props.historical_sales_context?.[0]?.two_weeks_ago
                      ?.actual_sales?.value
                  }
                  weather={
                    props.historical_sales_context?.[0]?.two_weeks_ago
                      ?.weather
                  }
                  events={
                    props.historical_sales_context?.[0]?.two_weeks_ago?.events
                  }
                />
                <HistoricalSalesContextCard
                  date={
                    props.historical_sales_context?.[0]?.three_weeks_ago
                      ?.date
                  }
                  initial_forecast={
                    props.historical_sales_context?.[0]?.three_weeks_ago
                      ?.initial_forecasted?.value
                  }
                  adjusted_forecast={
                    props.historical_sales_context?.[0]?.three_weeks_ago
                      ?.forecasted_adjustment
                  }
                  actual_sales={
                    props.historical_sales_context?.[0]?.three_weeks_ago
                      ?.actual_sales?.value
                  }
                  weather={
                    props.historical_sales_context?.[0]?.three_weeks_ago
                      ?.weather
                  }
                  events={
                    props.historical_sales_context?.[0]?.three_weeks_ago
                      ?.events
                  }
                />
                <HistoricalSalesContextCard
                  date={
                    props.historical_sales_context?.[0]?.four_weeks_ago?.date
                  }
                  initial_forecast={
                    props.historical_sales_context?.[0]?.four_weeks_ago
                      ?.initial_forecasted?.value
                  }
                  adjusted_forecast={
                    props.historical_sales_context?.[0]?.four_weeks_ago
                      ?.forecasted_adjustment
                  }
                  actual_sales={
                    props.historical_sales_context?.[0]?.four_weeks_ago
                      ?.actual_sales?.value
                  }
                  weather={
                    props.historical_sales_context?.[0]?.four_weeks_ago
                      ?.weather
                  }
                  events={
                    props.historical_sales_context?.[0]?.four_weeks_ago?.events
                  }
                />
              </div>
            </div>
            {hasMeaningfulReservationContext && (
              <div className="border-t border-slate-100 pt-4 mt-4">
                <p className="text-sm font-semibold text-slate-900 mb-2">
                  {reservationSectionTitle}
                </p>
                <div className="rounded-lg border border-slate-200 px-3 py-2.5 bg-white">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-[10px] text-slate-500">
                      {props.date_name}
                    </p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-[0.16em]">
                      {reservationCardContextLabel}
                    </p>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-[9px] text-slate-500">Covers</p>
                      <p className="text-sm font-bold text-slate-900">
                        {selectedReservationSummary.total_covers}
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] text-slate-500">Reservations</p>
                      <p className="text-sm font-bold text-slate-900">
                        {selectedReservationSummary.total_reservations}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[9px] text-slate-500">Covers / reservation</p>
                      <p className="text-sm font-bold text-slate-900">
                        {Number.isFinite(coversPerReservation)
                          ? coversPerReservation.toFixed(1)
                          : "0.0"}
                      </p>
                    </div>
                  </div>
                  {(Number.isFinite(reservationAverageToShow) || Number.isFinite(coversAverageToShow)) && (
                    <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
                      {Number.isFinite(reservationAverageToShow) && (
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-[10px] text-slate-500">
                            Reservations vs {reservationComparisonLabel} ({formatReservationAverage(reservationAverageToShow)})
                          </p>
                          {Number.isFinite(reservationDeltaToShow) ? (
                            <div className={`flex items-center gap-1 text-[11px] font-semibold ${reservationDeltaToShow >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                              {adjustment_icon_small(reservationDeltaRatioToShow)}
                              <span>{formatSignedReservationDelta(reservationDeltaToShow)}</span>
                            </div>
                          ) : (
                            <span className="text-[11px] font-semibold text-slate-400">--</span>
                          )}
                        </div>
                      )}
                      {Number.isFinite(coversAverageToShow) && (
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-[10px] text-slate-500">
                            Covers vs {reservationComparisonLabel} ({formatReservationAverage(coversAverageToShow)})
                          </p>
                          {Number.isFinite(coversDeltaToShow) ? (
                            <div className={`flex items-center gap-1 text-[11px] font-semibold ${coversDeltaToShow >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                              {adjustment_icon_small(coversDeltaRatioToShow)}
                              <span>{formatSignedReservationDelta(coversDeltaToShow)}</span>
                            </div>
                          ) : (
                            <span className="text-[11px] font-semibold text-slate-400">--</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {(hasPriorHolidayReservationContext || (hasReservationDaysOutContext && Number.isFinite(daysOutComparableCount)) || (!hasReservationDaysOutContext && comparableDowSummaries.length > 0)) && (
                    <div className="mt-2 pt-2 border-t border-slate-100">
                      <p className="text-[10px] text-slate-500 leading-4">
                        {reservationComparableLabel}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="h-[210px]"></div>
        </div>
        <DrawerFooterContainerLarge>
          <LoadingButton
            fullWidth
            disabled={
              props.forecast_sales === 0 && props.manualSalesInputEnabled
                ? !adjustedForecast ||
                  adjustedForecast === "0" ||
                  adjustedForecast === "" ||
                  adjustedForecast === initial_forecast.toFixed(0)
                : ((!reason || reason === "") && value !== 0) ||
                  value === initial_state
            }
            loading={savingLoading}
            loadingPosition="end"
            endIcon={<SaveIcon />}
            variant="contained"
            onClick={() => submitAndClose()}
            sx={{
              textTransform: "none",
              borderRadius: "12px",
              paddingY: "14px",
              backgroundColor: "#6d28d9",
              "&:hover": {
                backgroundColor: "#5b21b6",
              },
              fontSize: "16px",
            }}
          >
            {hasManualInitialForecast
              ? "Update Forecast"
              : props.forecast_sales === 0 && props.manualSalesInputEnabled
              ? "Set Forecast"
              : "Save and Recalculate"}
          </LoadingButton>
          <div className="w-full mt-3">
            <ButtonOutlined text="Cancel" onClick={handleClose} />
          </div>
        </DrawerFooterContainerLarge>
      </div>
      <ManualIntradayDistributionModal
        open={openManualIntradayModal}
        handleClose={() => setOpenManualIntradayModal(false)}
        manualForecastTotal={parseFloat(adjustedForecast) || 0}
        hoo={props.hoo}
        dateStr={props.date_str}
        fetchDistribution={props.fetchManualIntradayDistribution}
        saveDistribution={props.saveManualIntradayDistribution}
      />

      {/* Manual Actual Sales Edit Modal */}
      <Modal
        open={openManualActualSalesModal}
        onClose={() => {
          if (!savingManualActualSales) {
            setOpenManualActualSalesModal(false);
          }
        }}
        aria-labelledby="edit-manual-actual-sales-modal"
        aria-describedby="edit-manual-actual-sales-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "calc(100% - 32px)",
            maxWidth: 420,
            bgcolor: "background.paper",
            border: "1px solid #E2E8F0",
            borderRadius: "16px",
            boxShadow: 24,
            outline: "none",
            overflow: "hidden",
          }}
        >
          <div className="border-b border-slate-100 px-6 py-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-700">
                  <EditIcon style={{ fontSize: "19px" }} />
                </span>
                <div>
                  <h3
                    id="edit-manual-actual-sales-modal"
                    className="text-lg font-semibold leading-6 text-slate-900"
                  >
                    Edit actual sales
                  </h3>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {props.date_name}
                  </p>
                </div>
              </div>
              <IconButton
                aria-label="Close actual sales editor"
                className="text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                onClick={() => setOpenManualActualSalesModal(false)}
                disabled={savingManualActualSales}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          </div>

          <div className="px-6 py-5">
            <p
              id="edit-manual-actual-sales-description"
              className="mb-4 text-sm leading-5 text-slate-600"
            >
              Enter the final sales amount for this day. It will replace any
              existing manual value.
            </p>

            <div>
              <label
                htmlFor="manual-actual-sales-input"
                className="mb-1.5 block text-sm font-medium text-slate-700"
              >
                Actual sales
              </label>
              <TextField
                id="manual-actual-sales-input"
                fullWidth
                autoFocus
                placeholder="0.00"
                value={editManualActualSalesValue}
                onChange={(event) =>
                  setEditManualActualSalesValue(event.target.value)
                }
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter" &&
                    isManualActualSalesValueValid &&
                    !savingManualActualSales
                  ) {
                    handleSaveManualActualSales();
                  }
                }}
                type="number"
                inputProps={{
                  step: 0.01,
                  min: 0,
                  inputMode: "decimal",
                }}
                InputProps={{
                  startAdornment: (
                    <span className="mr-2 text-lg font-semibold text-slate-400">
                      $
                    </span>
                  ),
                  sx: {
                    backgroundColor: "white",
                    borderRadius: "12px",
                    "& input": {
                      fontSize: "20px",
                      fontWeight: 650,
                      paddingTop: "14px",
                      paddingBottom: "14px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#CBD5E1",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#94A3B8",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6D28D9",
                      borderWidth: "2px",
                    },
                  },
                }}
              />
              <p className="mt-2 text-xs leading-5 text-slate-500">
                Rightwork will use this amount when calculating earned labor.
              </p>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <button
                type="button"
                className="flex-1 rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => setOpenManualActualSalesModal(false)}
                disabled={savingManualActualSales}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 ${
                  isManualActualSalesValueValid && !savingManualActualSales
                    ? "bg-violet-600 text-white hover:bg-violet-700"
                    : "cursor-not-allowed bg-slate-100 text-slate-400"
                }`}
                onClick={handleSaveManualActualSales}
                disabled={
                  savingManualActualSales || !isManualActualSalesValueValid
                }
              >
                {savingManualActualSales ? "Saving..." : "Save actual sales"}
              </button>
            </div>
          </div>
        </Box>
      </Modal>
    </Drawer>
  );
}

export default ForecastAdjustmentDrawer;
